// Pure rules for `components/generate_metadata.js` (no imports, so plain `node --test` can load them).

/**
 * `alternates.languages` (hreflang) of a page, or undefined when there is nothing to declare.
 *
 * - Only the site's languages that the template really serves: `supported_locales` (a site's own list, e.g.
 *   chawk-bazar ["fa", "en", "ar"]); without it every language of the backend (`all_langs`) is kept. The backend
 *   lists languages such as zh/de/he whose URLs a template cannot serve.
 * - Fewer than two languages: no hreflang (a page has no twin to point at).
 * - `x-default` points at the default language's URL (chawk-bazar and ciseco added it by hand).
 * - `indexable: false` (noindex, private page): no hreflang either.
 */
export const build_hreflang_languages = ({ locales, default_locale, origin, pathname, supported_locales, indexable = true }) => {
	if (!indexable) return undefined;
	const supported = Array.isArray(supported_locales) && supported_locales.length ? supported_locales : null;
	const list = [...new Set((Array.isArray(locales) ? locales : []).filter((lang) => typeof lang === "string" && lang))].filter(
		(lang) => !supported || supported.includes(lang),
	);
	if (list.length < 2) return undefined;

	const path = typeof pathname === "string" && pathname ? pathname.replace(/\/{2,}/g, "/") : "/";
	const languages = {};
	for (const lang of list) {
		languages[lang] = collapse_double_slashes(lang === default_locale ? `${origin}${path}` : `${origin}/${lang}${path}`);
	}
	if (languages[default_locale]) languages["x-default"] = languages[default_locale];
	return languages;
};

const MIME_TYPES = {
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	png: "image/png",
	webp: "image/webp",
	gif: "image/gif",
	avif: "image/avif",
	svg: "image/svg+xml",
	ico: "image/x-icon",
	bmp: "image/bmp",
	mp4: "video/mp4",
	webm: "video/webm",
	mov: "video/quicktime",
	ogv: "video/ogg",
	mp3: "audio/mpeg",
	wav: "audio/wav",
	ogg: "audio/ogg",
	m4a: "audio/mp4",
	aac: "audio/aac",
};

const extension_of = (url) =>
	typeof url === "string" ? url.split(/[#?]/)[0].split("/").pop()?.split(".").slice(1).pop()?.trim().toLowerCase() || "" : "";

/**
 * MIME type of an og image / video / audio or of the favicon, or undefined when it cannot be told. It was built as
 * `${media.information.type}/${extension}`, so a media without `information.type` gave `undefined/webp` on every page
 * (chawk-bazar / ciseco removed it by hand) and `jpg` gave the non-existent `image/jpg`. The file's extension wins; a
 * panel value (`fallback`, e.g. the favicon's `type`) is used only when the extension is unknown and it looks valid.
 */
export const media_mime_type = ({ url, extension, info_type, fallback } = {}) => {
	const ext = String(extension || extension_of(url) || "").toLowerCase().replace(/^\./, "");
	if (MIME_TYPES[ext]) return MIME_TYPES[ext];
	if (typeof fallback === "string" && /^[a-z]+\/[\w.+-]+$/i.test(fallback.trim())) return fallback.trim();
	if (["image", "video", "audio"].includes(info_type) && /^[a-z0-9]{2,5}$/.test(ext)) return `${info_type}/${ext}`;
	return undefined;
};

/** A backend date (ms timestamp as number or string, or an ISO string) → ISO string, or undefined. */
export const to_iso_timestamp = (value) => {
	if (value === null || value === undefined || value === "") return undefined;
	const date = typeof value === "number" || /^\d+$/.test(String(value).trim()) ? new Date(Number(value)) : new Date(String(value));
	return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const NAMED_ENTITIES = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };

/** Plain text of a backend title / summary (tags removed, entities decoded, spaces collapsed). */
export const meta_text = (value) =>
	(typeof value === "string" ? value : "")
		.replace(/<[^>]*>/g, " ")
		.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity) => {
			if (entity[0] === "#") {
				const code = entity[1].toLowerCase() === "x" ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
				return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : match;
			}
			return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
		})
		.replace(/\s+/g, " ")
		.trim();

// chawk-bazar / ciseco (`decode_entities` in helpers/blogArticle.js, used on article image urls): only the entity step
// of `meta_text`, so tags and spaces stay as they are. Same entities and rules as `meta_text`.
/** HTML entities (`&amp;`, `&#1601;`, `&#x41;`, `&nbsp;` ...) to characters; unknown ones stay. Non-string → "". */
export const decode_html_entities = (value) =>
	String(value ?? "").replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity) => {
		if (entity[0] === "#") {
			const code = entity[1].toLowerCase() === "x" ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
			return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : match;
		}
		return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
	});

/** A meta description of at most `max` characters, cut at a word. */
export const trim_meta_description = (text, max = 160) => {
	const value = meta_text(text);
	if (value.length <= max) return value;
	const cut = value.slice(0, max - 1);
	const space = cut.lastIndexOf(" ");
	return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trim()}…`;
};

const absolute = (base, path) => {
	if (typeof path !== "string" || !path) return undefined;
	if (/^https?:\/\//i.test(path)) return path;
	return `${String(base || "").replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
};

/**
 * Absolute URL of a panel field such as `canonical_url` / `og_url` / `twitter_url`, or `fallback` when it is empty. It
 * was `${origin}/${value}`, so a value starting with "/" gave `https://site//path` and a full URL gave
 * `https://site/https://...` (chawk-bazar / ciseco cleaned it with `normalize_meta_urls`).
 */
export const meta_url = (origin, value, fallback) => {
	const text = typeof value === "string" ? value.trim() : "";
	return collapse_double_slashes(text ? absolute(origin, text) : fallback);
};

/**
 * The page number of a listing URL (`?page=3` → 3); 1 when missing or invalid. `searchParams` is Next's object.
 */
export const listing_page_number = (searchParams) => {
	const raw = Array.isArray(searchParams?.page) ? searchParams.page[0] : searchParams?.page;
	const page = parseInt(raw, 10);
	return Number.isFinite(page) && page > 1 ? page : 1;
};

/**
 * All sites (core-issues item "canonical — query params other than page", chawk-bazar / ciseco had it locally): page 2+
 * of a listing is its own canonical (`/x/?page=2`), page 1 and every other query (filter, sort, tab, utm) the clean URL.
 * It used to drop `?page=` too, so every page of a listing pointed to page 1.
 */
export const canonical_with_page = (url, searchParams) => {
	const page = listing_page_number(searchParams);
	return page > 1 && typeof url === "string" && !url.includes("?") ? `${url}?page=${page}` : url;
};

/** `https://site.com//en//x/` → `https://site.com/en/x/` (only the path; the `//` after the scheme stays). */
export const collapse_double_slashes = (url) => {
	if (typeof url !== "string") return url;
	const match = url.match(/^(https?:\/\/[^/]+)(\/.*)?$/i);
	if (!match) return url;
	return `${match[1]}${(match[2] || "").replace(/\/{2,}/g, "/")}`;
};

const list = (value) => (Array.isArray(value) ? value : typeof value === "string" && value ? [value] : []).map(meta_text).filter(Boolean);

/**
 * Open Graph data of a blog article page (`og:type=article`). `generate_metadata` built `article:*` only from the
 * page's SEO record, whose article fields are empty unless someone fills them in the panel, set no `og:type`, and used
 * the site logo as the image of an article without its own OG image. chawk-bazar / ciseco rebuilt all of it from the
 * article (`apply_blog_meta`); it is done here now for every site.
 *
 * The SEO record wins wherever the panel filled a field; the article itself fills the rest. Returns null without an
 * article.
 * @returns {{ title: string, description: string, article: object, image?: { url, alt, width?, height? } } | null}
 */
export const build_article_meta = ({ blog, seo = {}, origin = "", storage_prefix_url = "" } = {}) => {
	if (!blog || typeof blog !== "object") return null;
	const title = meta_text(blog.title);

	const published = to_iso_timestamp(seo?.article_published_time) || to_iso_timestamp(blog.publishAt) || to_iso_timestamp(blog.createdAt);
	let modified = to_iso_timestamp(seo?.article_modified_time) || to_iso_timestamp(blog.updatedAt) || published;
	// an edit dated before the publish date (back-dated article) is the publish date
	if (published && modified && modified < published) modified = published;

	const panel_authors = list(seo?.article_author);
	const author_name = meta_text(blog.user?.full_name);
	const author_url = blog.user?.seo?.full_url ? absolute(origin, blog.user.seo.full_url) : undefined;
	const authors = panel_authors.length ? panel_authors : author_url || author_name ? [author_url || author_name] : [];

	const panel_tags = list(seo?.article_tag);
	const tags = panel_tags.length ? panel_tags : [...new Set(list(blog.seo?.keywords))];

	const info = blog.media?.media?.information;
	const size = (value) => {
		const number = parseInt(value);
		return number > 0 && number < 20000 ? number : undefined;
	};
	const image_url = blog.media?.url ? absolute(storage_prefix_url, blog.media.url) : undefined;

	const article = {
		publishedTime: published,
		modifiedTime: modified,
		expirationTime: to_iso_timestamp(seo?.article_expiration_time),
		authors: authors.length ? authors : undefined,
		section: meta_text(seo?.article_section) || meta_text(blog.category?.title) || undefined,
		tags: tags.length ? tags : undefined,
	};

	return {
		title,
		description: trim_meta_description(blog.seo?.description) || trim_meta_description(blog.summary),
		article: Object.fromEntries(Object.entries(article).filter(([, value]) => value !== undefined)),
		image: image_url
			? {
				url: image_url,
				alt: meta_text(blog.media?.alt) || title || undefined,
				width: size(info?.width),
				height: size(info?.height),
				type: media_mime_type({ url: image_url, extension: blog.media?.media?.extension, info_type: info?.type }),
			}
			: undefined,
	};
};
